import type { QueryClient } from "@tanstack/react-query";

import { appQueriesStore } from "@/app/api";
import { privateAPI } from "@/shared/lib";
import type { ServiceResponse } from "@/shared/lib/axios";
import type { AvailableLanguage } from "@/shared/models";
import type { Patient, PatientFormValues } from "@/shared/models/legacy-types";

const parsePatientFormValues = (values: PatientFormValues) => {
  const {
    email,
    firstName,
    lastName,
    dateOfBirth,
    gender,
    phoneNumber,

    city,
    state,
    zip,
    line1,
    line2,

    timezone,

    cardFrontImage,
    cardBackImage,
    cardFrontId,
    cardBackId,
  } = values;

  return {
    email,
    firstName,
    lastName,
    dateOfBirth,
    gender,
    phoneNumber,
    location: {
      city,
      state,
      zip,
      line1,
      line2,
    },
    timezone,
    policy: {
      cardFrontImage,
      cardBackImage,
      cardFrontId,
      cardBackId,
    },
  };
};

const invalidatesWithPortalUserQuery = (queryClient: QueryClient) => {
  void queryClient.invalidateQueries({
    queryKey: appQueriesStore.patient.queryKey,
  });
  void queryClient.invalidateQueries({
    queryKey: appQueriesStore.user.queryKey,
  });
};

export const updatePatient = {
  mutation: async (params: PatientFormValues) => {
    const { data } = await privateAPI.put<ServiceResponse<Patient>>(
      "/patients",
      parsePatientFormValues(params),
    );

    return data.data;
  },
  invalidates: invalidatesWithPortalUserQuery,
};

export const updatePatientLanguagePreference = {
  mutation: async (language: AvailableLanguage) => {
    return await privateAPI.put("/patients/language", { language });
  },
  invalidates: invalidatesWithPortalUserQuery,
};

interface UploadInsuranceCardParams {
  insuranceCardFront?: File;
  insuranceCardBack?: File;
}

export const uploadInsuranceCard = {
  mutation: async (insuranceCard: UploadInsuranceCardParams) => {
    const { data } = await privateAPI.post<
      ServiceResponse<{ cardFrontUrl?: string; cardBackUrl?: string }>
    >(
      "/patients/upload-insurance-card",
      { ...insuranceCard },
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    return data.data;
  },
  invalidates: (queryClient: QueryClient) => {
    void queryClient.invalidateQueries({
      queryKey: appQueriesStore.patient.queryKey,
    });
  },
};
